import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { RS_TIMEFRAMES } from '../../constants/relativeStrengthTimeframes';
import useIsMobile from '../../hooks/useIsMobile';
import SegmentedControl from '../shared/SegmentedControl';
import RsGroupChart, { RS_TICKER_LINE_COLORS } from '../../research/rsStudy/components/RsGroupChart';

const LIVE_HINT = 'The top half is the equal-weight price of these symbols from the first bar, as percent change, next to the benchmark. The bottom half is relative strength from 0 to 100 versus the benchmark. Green is strength above 50; red is weakness below 50.';

const TIMEFRAME_OPTIONS = RS_TIMEFRAMES.map((tf) => ({
  value: tf.key,
  label: tf.label,
}));

const DEFAULT_ON_CAP = 8;

function latestScore(points) {
  const last = points?.[points.length - 1];
  return Number.isFinite(last?.score) ? last.score : null;
}

function defaultOn(ticker, tickers, enabled, highlightedTicker) {
  if (enabled[ticker] === true) return true;
  if (enabled[ticker] === false) return false;
  if (tickers.length <= DEFAULT_ON_CAP) return true;
  return ticker === highlightedTicker;
}

/**
 * Rolling RS history for the live Relative Strength page.
 * Timeframe picks the lookback window; symbols overlay the study-style
 * basket-versus-benchmark chart.
 */
export default function RelativeStrengthHistoryChart({
  tickers = [],
  seriesByTicker = {},
  averageRs = [],
  groupIndex = [],
  benchmarkIndex = [],
  benchmark = 'SPY',
  timeframeKey = 'month',
  onTimeframeChange,
  highlightedTicker = null,
  loading = false,
  error = null,
  height = 440,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [enabled, setEnabled] = useState({});

  const list = useMemo(
    () => (tickers || []).map((ticker) => String(ticker).toUpperCase()).filter(Boolean),
    [tickers],
  );

  useEffect(() => {
    setEnabled((prev) => {
      const next = {};
      list.forEach((ticker) => {
        if (prev[ticker] === true || prev[ticker] === false) next[ticker] = prev[ticker];
      });
      return next;
    });
  }, [list]);

  const tickerSeries = useMemo(
    () => list
      .filter((ticker) => defaultOn(ticker, list, enabled, highlightedTicker))
      .map((ticker) => ({
        ticker,
        color: RS_TICKER_LINE_COLORS[list.indexOf(ticker) % RS_TICKER_LINE_COLORS.length],
        points: seriesByTicker[ticker] || [],
      }))
      .filter((row) => row.points.length > 0),
    [list, enabled, highlightedTicker, seriesByTicker],
  );

  const hasData = averageRs.length > 0 || tickerSeries.length > 0;

  return (
    <div data-testid="relative-strength-history">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: colors.text }}>
            Over time
          </div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Rolling score versus {benchmark}. Top pane is the list versus {benchmark}.
          </div>
        </div>
        <SegmentedControl
          options={TIMEFRAME_OPTIONS}
          value={timeframeKey}
          onChange={(next) => onTimeframeChange?.(next)}
          size="sm"
          scrollable
          fullWidth={false}
          ariaLabel="History timeframe"
          testId="relative-strength-history-timeframe"
        />
      </div>

      {list.length > 0 ? (
        <div
          data-testid="relative-strength-history-tickers"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: 8,
          }}
        >
          {list.map((ticker, index) => {
            const on = defaultOn(ticker, list, enabled, highlightedTicker);
            const score = latestScore(seriesByTicker[ticker]);
            const color = RS_TICKER_LINE_COLORS[index % RS_TICKER_LINE_COLORS.length];
            return (
              <button
                key={ticker}
                type="button"
                data-testid={`relative-strength-history-toggle-${ticker}`}
                aria-pressed={on}
                onClick={() => setEnabled((prev) => ({
                  ...prev,
                  [ticker]: !defaultOn(ticker, list, prev, highlightedTicker),
                }))}
                style={{
                  border: `1px solid ${on ? color : colors.border}`,
                  background: ticker === highlightedTicker ? `${color}22` : 'transparent',
                  color: on ? colors.text : colors.textMuted,
                  borderRadius: 999,
                  padding: isMobile ? '6px 10px' : '4px 10px',
                  minHeight: isMobile ? 32 : undefined,
                  fontSize: 12,
                  cursor: 'pointer',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {ticker}
                {Number.isFinite(score) ? ` ${Math.round(score)}` : ''}
              </button>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <div style={{ color: colors.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>
      ) : null}

      {loading && !hasData ? (
        <div
          data-testid="relative-strength-history-loading"
          style={{
            height,
            display: 'flex',
            alignItems: 'center',
            color: colors.textMuted,
            fontSize: 13,
          }}
        >
          Loading history…
        </div>
      ) : (
        <RsGroupChart
          averageRs={averageRs}
          groupIndex={groupIndex}
          benchmarkIndex={benchmarkIndex}
          tickerSeries={tickerSeries}
          highlightedTicker={highlightedTicker}
          benchmark={benchmark}
          threshold={null}
          height={height}
          hint={LIVE_HINT}
          testId="relative-strength-history-chart"
          emptyTestId="relative-strength-history-empty"
        />
      )}
    </div>
  );
}
